import {computed, signal} from "@preact/signals-react";
import {
    ICheckoutPayload,
    IEventTicketCategoryForBuy,
    TicketOrderSummary,
    TicketOrderSummaryItem,
} from "@/app/dashboard/models/EventTicketCategory";
import {freeCheckout, getCheckoutObject, getEventTicketsCategoryByEventId,} from "@/services/event-tickets-service";
import {showCommonApiError, showError, showSuccess} from "@/lib/toast-utill";
import {getProfile, updateProfile} from "@/services/user-service";
import {IProfile} from "@/app/dashboard/models/User";
import {profile} from "@/shared/features/profile/store/profile.store";
import {Stripe, StripeElements} from "@stripe/stripe-js";
import {ICoupon} from "@/app/dashboard/models/Coupon";
import {getCouponCodeData} from "@/services/coupon-service";
import {affiliateLink, eventInSingleEventPage} from "@/app/events/store/single-page.store";
import {clearLoginFormModalDetails} from "@/store/auth.store";
import {getErrorDataFromResponse} from "@/services/common-api";
import {selectedSeats} from "@/app/events/[id]/store/buy-ticket-seating.store";

export enum BuyTicketsSteps {
    SelectTicket,
    PaymentDetails,
    LoginForm,
}

export const eventTicketForCurrentEvent = signal<IEventTicketCategoryForBuy[]>(
    []
);


export const totalPurchasedTicketsForCurrentEvent = signal<number>(0)
export const totalNumberOfTickets = computed(() => {
    return eventTicketForCurrentEvent.value.reduce(
        (acc, item) => acc + item.quantity,
        totalPurchasedTicketsForCurrentEvent.value
    );
})
export const buyTicketCurrentStep = signal<BuyTicketsSteps>(
    BuyTicketsSteps.SelectTicket
);

export const couponCodeData = signal<ICoupon>();
export const couponCode = signal<string>();

export const isLoading = signal<boolean>();
export const isGetTicketDialogOpen = signal<boolean>(false);
export const isPaymentTimedOut = signal<boolean>(false);

export const showLoader = () => (isLoading.value = true);
export const hideLoader = () => (isLoading.value = false);

export const getTicketDialogVisibility = () => {
    isGetTicketDialogOpen.value = !isGetTicketDialogOpen.value
    if (!isGetTicketDialogOpen.value) {
        clearInPageData();
    }
}

const getDiscountAmount = (initialAmount: number, couponData: ICoupon) => {
    if (!couponData) {
        return 0;
    }

    if (couponData?.amountOff > 0) {
        return couponData.amountOff < initialAmount
            ? couponData.amountOff
            : initialAmount;
    }

    if (couponData?.percentOff > 0) {
        return (initialAmount * couponData.percentOff) / 100;
    }

    return 0;
};

const getDiscountText = (couponData: ICoupon) => {
    if (couponData?.amountOff > 0) {
        return `${couponData.amountOff} off`;
    }

    if (couponData?.percentOff > 0) {
        return `${couponData.percentOff}% off`;
    }

    return "";
};

export const transactionFeePercentage = computed(
    () => (+eventInSingleEventPage?.value?.transactionFee || 0) / 100
);

export const transactionFeeText = computed(() => {
    const transactionFee = ticketOrderSummary.value?.transactionFee ?? 0
    if (transactionFee && typeof transactionFee === "number" && transactionFee > 0) {
        return Number(transactionFee).toFixed(2);
    }
    return '0';
});

export const ticketOrderSummary = computed<TicketOrderSummary>(() => {
    const orderItems: TicketOrderSummaryItem[] = (eventTicketForCurrentEvent.value ?? [])
        .filter((e) => e.quantity)
        .map((item) => ({
            ...item,
            totalPrice: item.price * item.quantity,
            seats:  selectedSeats.value.filter(s => s.ticketCategoryId === item.id)
        }));

    const total = orderItems.reduce((total, item) => total + item.totalPrice, 0);

    const transactionFee = total * transactionFeePercentage.value;
    const totalBeforeDiscount = total + transactionFee;

    const discount = getDiscountAmount(totalBeforeDiscount, couponCodeData.value);

    const netAmount = totalBeforeDiscount - discount;

    const discountText = getDiscountText(couponCodeData.value);

    const currencyFormat = orderItems[0]?.currencyFormat;

    return {
        orderItems,
        total,
        transactionFee,
        discount,
        netAmount,
        discountText,
        currencyFormat,
    };
});

export const profileForTicketOrder = signal<IProfile>();

export const isContactFormValid = computed(() => {
    const profile = profileForTicketOrder.value;
    console.log(profile);
    return profile.firstName && profile.lastName && profile.contactNumber
})

export const patchProfileValues = (p: Partial<IProfile>) => {
    profile.value = {...profile.value, ...p};
};


export const patchProfileTicketOrderValues = (p: Partial<IProfile>) => {
    profileForTicketOrder.value = {...profileForTicketOrder.value, ...p};
};


export const updateContactDetails = async () => {
    showLoader()
    const userInputProfile = profileForTicketOrder.value
    const [, error] = await updateProfile({
        lastName: userInputProfile.lastName,
        firstName: userInputProfile.firstName,
        contactNumber: userInputProfile.contactNumber,
    })
    hideLoader()

    if (error) {
        return showCommonApiError("updating contact details")
    }
}

export const navigateToStep = (step: BuyTicketsSteps) => {
    buyTicketCurrentStep.value = step;
};

export const loadProfileToOrder = async () => {
    const [profile, error] = await getProfile();
    if (error) {
        return showCommonApiError("loading profile");
    }
    profileForTicketOrder.value = profile;
    await loadCheckoutObject();
};

export const loadEventTickets = async (eventId: string) => {
    const [ticketCategoryList, error] = await getEventTicketsCategoryByEventId(
        eventId
    );
    if (error && error.message !== "Request failed with status code 422") {
        console.error(error);
        return showCommonApiError("loading ticket types");
    }

    eventTicketForCurrentEvent.value = ticketCategoryList.list.map((e) => ({
        ...e,
        quantity: 0,
    }));
};

export const increaseQuantity = (id: string) => {
    const ticketList = eventTicketForCurrentEvent.value;
    const ticketCategoryForBuy = ticketList.find((e) => e.id === id);
    if (!ticketCategoryForBuy) {
        return;
    }
    ticketCategoryForBuy.quantity++;
    eventTicketForCurrentEvent.value = [...ticketList];
};

export const decreaseQuantity = (id: string) => {
    const ticketList = eventTicketForCurrentEvent.value;
    const ticketCategoryForBuy = ticketList.find((e) => e.id === id);
    if (!ticketCategoryForBuy) {
        return;
    }
    if (ticketCategoryForBuy.quantity === 0) {
        return;
    }
    ticketCategoryForBuy.quantity--;
    eventTicketForCurrentEvent.value = [...ticketList];
};

export const selectTicketPageContinue = () => {
    navigateToStep(BuyTicketsSteps.PaymentDetails);
};
export const loginToContinue = () => {
    navigateToStep(BuyTicketsSteps.LoginForm);
};

export const submitFreeCheckout = async (navigation: Function) => {
    await updateContactDetails();
    const checkoutPayloadData = checkoutPayload.value;
    if (
        !checkoutPayloadData?.isFreeCheckout ||
        !checkoutPayloadData.paymentRecordId
    ) {
        return;
    }

    showLoader();
    const [, error] = await freeCheckout(checkoutPayloadData.paymentRecordId);
    hideLoader();
    if (error) {
        return showCommonApiError("failed to checkout");
    }

    navigation(
        `/payment-success?paymentRecordId=${checkoutPayloadData.paymentRecordId}`
    );
};

export const checkoutPayload = signal<ICheckoutPayload>();

export const loadCheckoutObject = async () => {
    const [response, error] = await getCheckoutObject(
        ticketOrderSummary.value.orderItems.map((e) => ({
            id: e.id,
            quantity: e.quantity,
            seats: e.seats
        })),
        affiliateLink.value,
        couponCodeData?.value?.name ?? "",
    );
    if (!response || error) {
        const errorObject = getErrorDataFromResponse(error);
        if (errorObject.purchasedTicketCount) {
            totalPurchasedTicketsForCurrentEvent.value = errorObject.purchasedTicketCount
        }
        if (errorObject.isUIError) {
            navigateToStep(BuyTicketsSteps.SelectTicket)
            return showError(errorObject.message, "Something wrong while ticket purchasing");
        }
        navigateToStep(BuyTicketsSteps.SelectTicket)
        return;
    }
    checkoutPayload.value = response;
};

export const submitPayment = async (
    elements: StripeElements,
    stripe: Stripe
) => {

    showLoader()
    await updateContactDetails()
    const {error: submitError} = await elements.submit();
    if (submitError) {
        return console.error(submitError.message);
    }

    const {error} = await stripe.confirmPayment({
        elements,
        clientSecret: checkoutPayload.value?.clientSecret,

        confirmParams: {
            return_url: `${location.origin}/payment-success`,
        },
    });

    hideLoader();

    if (error) {
        console.error(error.message);
    }
};

export const setCouponCode = (code: string) => (couponCode.value = code);

export const isCouponCodeAdded = computed(() => {
    const couponData = couponCodeData.value;
    return couponData?.amountOff > 0 || couponData?.percentOff > 0;
});

export const discountAmount = computed(() => {
    const couponData = couponCodeData.value;

    if (couponData?.amountOff > 0) {
        return couponData.amountOff;
    }

    if (couponData?.percentOff > 0) {
        return ticketOrderSummary.value.total * couponData.percentOff;
    }
});

export const addCouponCode = async () => {
    if (!couponCode.value) {
        return;
    }

    const [couponData, error] = await getCouponCodeData(
        couponCode.value,
        eventInSingleEventPage.value.id
    );

    if (error) {
        return showCommonApiError("adding coupon code");
    }

    if (!couponData) {
        return showError("Invalid coupon code");
    }

    if (couponData?.amountOff > 0 || couponData?.percentOff > 0) {
        couponCodeData.value = couponData;
        return showSuccess("Coupon code added successfully!");
    }

    return showError("Invalid coupon code");
};

export const clearCouponCode = async () => {
    couponCodeData.value = null;
    couponCode.value = "";
};

export const clearInPageData = () => {
    clearCouponCode();
    buyTicketCurrentStep.value = undefined;
    profileForTicketOrder.value = undefined;
    checkoutPayload.value = undefined;
    navigateToStep(BuyTicketsSteps.SelectTicket);
    clearLoginFormModalDetails()
    totalPurchasedTicketsForCurrentEvent.value = 0
}

export const clearAllData = () => {
    clearCouponCode();
    eventInSingleEventPage.value = undefined;
    eventTicketForCurrentEvent.value = undefined;
    buyTicketCurrentStep.value = undefined;
    profileForTicketOrder.value = undefined;
    checkoutPayload.value = undefined;
    totalPurchasedTicketsForCurrentEvent.value = 0
    navigateToStep(BuyTicketsSteps.SelectTicket);
    clearLoginFormModalDetails()
};

export const setIsTimeoutPayment = (isRunning: boolean) => {
    isPaymentTimedOut.value = !isRunning;
};
